import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { defineFillAction, type FillFields } from '@/shared/form/agent-fill-action';
import { action, can } from '@/shared/screen/define';

// O mesmo `SIMULATION_READ` de `simulation.screen` (herdado de `/ccb`), aqui sem importar o manifesto (sem ciclo).
const SIMULATION_READ = can('CreditNote', 'Read');

// O Luy no Contratar (U56, onda L4 da discovery/15): um preenchimento `draft` por etapa, a busca `read` do que se
// escolhe por id e o "avançar" `draft`. Arquivo leve (entra nos `*.screen.ts`): só zod, Lingui e o manifesto; o
// `run` de verdade é o da tela montada (`useHireAgent`, `simulation-hire.agent.ts`), que reconfere a etapa. Os campos
// vão no formato do formulário: CPF só com os dígitos (U56 permite), centavos inteiros, data civil AAAA-MM-DD.

const cpf = z.string().regex(/^\d{11}$/);
const civilDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const cents = z.number().int().min(0);
const positiveInt = z.number().int().positive();
const text = (max: number) => z.string().trim().min(1).max(max);

const MOUNTED = msg`Abra a etapa da contratação para o Luy preencher.`;
const mountedRequired = (): never => {
  throw new Error(i18n._(MOUNTED));
};

const fill = <F extends FillFields>(id: string, description: ReturnType<typeof msg>, fields: F) =>
  defineFillAction({ id, description, fields, permission: SIMULATION_READ, mountedMessage: MOUNTED });

/** Convênio pelo id (a busca `searchAgreement` devolve os ids). */
export const agreementFill = (prefix: string) =>
  fill(
    `${prefix}.fillAgreement`,
    msg`Etapa Convênio: escolhe o convênio pelo id (payrollAgreementCode), como rascunho. O id sai da ação searchAgreement desta tela. Não troca o que a pessoa já escolheu. Avançar de etapa é a ação next.`,
    { payrollAgreementCode: { schema: z.string().min(1), label: msg`Convênio` } },
  );

/** A busca de convênios da etapa Convênio: a mesma consulta do campo (só leitura, gratuita). */
export const agreementSearch = (prefix: string) =>
  action<{ readonly query?: string | undefined }>({
    id: `${prefix}.searchAgreement`,
    kind: 'read',
    label: msg`Buscar convênio`,
    description: msg`Busca convênios pelo nome (query; vazio lista os mais recentes), a mesma busca do campo Convênio. Devolve id, nome e averbadora; use o id em fillAgreement.`,
    permission: SIMULATION_READ,
    input: z.strictObject({ query: z.string().trim().max(100).optional() }),
    run: mountedRequired,
  });

const borrowerText = msg`Etapa Tomador: preenche, como rascunho, os dados do tomador. CPF só com os 11 dígitos, sem máscara. Não troca o que a pessoa já editou. Avançar de etapa é a ação next.`;

/** Tomador do consignado público e dos refinanciamentos: CPF e matrícula. */
export const payrollBorrowerFill = (prefix: string) =>
  fill(`${prefix}.fillBorrower`, borrowerText, {
    registrationNumber: { schema: cpf, label: msg`CPF` },
    employeeCode: { schema: text(40), label: msg`Matrícula` },
  });

/** Tomador do privado e do CDC: só o CPF. */
export const cpfBorrowerFill = (prefix: string) =>
  fill(`${prefix}.fillBorrower`, borrowerText, { registrationNumber: { schema: cpf, label: msg`CPF` } });

/** Tomador do INSS: CPF, número do benefício, telefone (11 dígitos, com DDD) e nome. */
export const inssBorrowerFill = (prefix: string) =>
  fill(`${prefix}.fillBorrower`, borrowerText, {
    registrationNumber: { schema: cpf, label: msg`CPF` },
    benefitNumber: { schema: z.string().regex(/^\d{1,20}$/), label: msg`Número do benefício` },
    phone: { schema: z.string().regex(/^\d{11}$/), label: msg`Telefone` },
    name: { schema: text(200), label: msg`Nome` },
  });

/** Tomador do veículo: o CPF (a busca da pessoa é a da tela, gratuita). */
export const vehicleBorrowerFill = (prefix: string) =>
  fill(`${prefix}.fillBorrower`, borrowerText, { registrationNumber: { schema: cpf, label: msg`CPF da pessoa` } });

const simulationText = msg`Etapa Simulação: preenche, como rascunho, o valor da margem a simular (centavos) e as datas (AAAA-MM-DD). Não simula: o Continuar desta etapa simula as ofertas e é sempre da pessoa.`;

/** Simulação dos consignados públicos: valor da margem, início e primeira parcela. */
export const payrollSimulationFill = (prefix: string) =>
  fill(`${prefix}.fillSimulation`, simulationText, {
    valueMarginReserve: { schema: cents, label: msg`Valor da margem` },
    startDate: { schema: civilDate, label: msg`Data de início` },
    firstPaymentDate: { schema: civilDate, label: msg`Data da primeira parcela` },
  });

/** Simulação do INSS, do privado e do CDC: valor da margem e início. */
export const offersSimulationFill = (prefix: string) =>
  fill(`${prefix}.fillSimulation`, simulationText, {
    valueMarginReserve: { schema: cents, label: msg`Valor da margem` },
    startDate: { schema: civilDate, label: msg`Data de início` },
  });

/** Veículo: renda, entrada (Financiamento), placa e os dados do bem. Não busca a placa. */
export const vehicleFill = (prefix: string, financing: boolean) =>
  fill(
    `${prefix}.fillVehicle`,
    msg`Etapa Veículo: preenche, como rascunho, a renda comprovada e a entrada (centavos; entrada só no Financiamento), a placa, o Renavam, o ano, a marca (nome como a lista da tela), o modelo (código FIPE), o chassi, o combustível, a cor, as UFs da placa e da operação e o uso (particular ou outro). Não busca a placa (o botão Buscar é da pessoa). Não simula: o Continuar desta etapa simula as ofertas e é sempre da pessoa.`,
    {
      ...(financing ? { downPayment: { schema: cents, label: msg`Entrada` } } : {}),
      income: { schema: cents, label: msg`Renda comprovada` },
      plate: { schema: z.string().regex(/^[A-Za-z0-9]{7}$/), label: msg`Placa` },
      renavam: { schema: z.string().regex(/^\d{9,11}$/), label: msg`Renavam` },
      year: { schema: z.number().int().min(1950).max(2100), label: msg`Ano do veículo` },
      brand: { schema: text(80), label: msg`Marca do veículo` },
      modelFipeCode: { schema: text(20), label: msg`Modelo do veículo` },
      chassis: { schema: z.string().regex(/^[A-Za-z0-9]{17}$/), label: msg`Chassis` },
      typeOfFuel: { schema: text(20), label: msg`Tipo de combustível` },
      color: { schema: text(10), label: msg`Cor do veículo` },
      plateState: { schema: z.string().regex(/^[A-Z]{2}$/), label: msg`UF Placa` },
      operationState: { schema: z.string().regex(/^[A-Z]{2}$/), label: msg`UF Operação` },
      utility: { schema: z.enum(['particular', 'outro']), label: msg`Uso do veículo` },
    },
  );

/** "Editar proposta" dos Detalhes do consignado público, preparado pelo Luy; "Atualizar proposta" é da pessoa. */
export const payrollDetailsFill = (prefix: string) =>
  action<{ readonly typeInstallment?: 'installment' | 'valueFree' | undefined; readonly installmentValue?: number | undefined; readonly numberOfPayments?: number | undefined }>({
    id: `${prefix}.fillDetails`,
    kind: 'draft',
    label: msg`Preparar a edição da proposta`,
    description: msg`Etapa Detalhes, com uma oferta escolhida: abre o Editar proposta já preenchido com o cálculo (typeInstallment: installment por parcela ou valueFree por valor liberado), o valor da parcela (centavos) e a quantidade de parcelas. Não simula: o Atualizar proposta é da pessoa.`,
    permission: SIMULATION_READ,
    input: z
      .strictObject({
        typeInstallment: z.enum(['installment', 'valueFree']).optional(),
        installmentValue: cents.positive().optional(),
        numberOfPayments: positiveInt.optional(),
      })
      .refine((value) => Object.values(value).some((item) => item !== undefined)),
    run: mountedRequired,
  });

/** O valor da proposta do veículo, preparado no Editar proposta; "Atualizar proposta" é da pessoa. */
export const vehicleProposalFill = (prefix: string) =>
  action<{ readonly valueInCents: number }>({
    id: `${prefix}.fillProposal`,
    kind: 'draft',
    label: msg`Preparar a edição da proposta`,
    description: msg`Etapa Proposta: abre o Editar proposta já preenchido com o valor para simulação (centavos), dentro do limite disponível. Não simula: o Atualizar proposta é da pessoa.`,
    permission: SIMULATION_READ,
    input: z.strictObject({ valueInCents: cents.positive() }),
    run: mountedRequired,
  });

/** Crédito pessoal e FGTS: o produto de crédito pelo id (a busca `searchProduct` devolve os ids). */
export const creditProductFill = (prefix: string) =>
  fill(
    `${prefix}.fillProduct`,
    msg`Etapa Produto de crédito: escolhe o produto pelo id (productId), como rascunho; a tela aplica os padrões do produto. O id sai da ação searchProduct.`,
    { productId: { schema: z.string().min(1), label: msg`Produto` } },
  );

export const creditProductSearch = (prefix: string) =>
  action<{ readonly query?: string | undefined }>({
    id: `${prefix}.searchProduct`,
    kind: 'read',
    label: msg`Buscar produto`,
    description: msg`Busca produtos de crédito pelo nome (query), a mesma busca do campo Produto. Devolve id e nome; use o id em fillProduct.`,
    permission: SIMULATION_READ,
    input: z.strictObject({ query: z.string().trim().max(100).optional() }),
    run: mountedRequired,
  });

/** Tomador do crédito pessoal e do FGTS: tipo de pessoa e, no FGTS, CPF, saldo e mês do aniversário. */
export const creditBorrowerFill = (prefix: string) =>
  fill(
    `${prefix}.fillBorrower`,
    msg`Etapa Tomador: preenche, como rascunho, o tipo de pessoa (legalPerson: false física, true jurídica) e, com produto FGTS, o CPF (11 dígitos, sem máscara), o saldo FGTS (centavos, em amortization.requestedAmount) e o mês do aniversário (amortization.paymentMonth, em inglês: January…December). O tomador cadastrado é escolhido pela pessoa no campo.`,
    {
      legalPerson: { schema: z.boolean(), label: msg`Tipo de pessoa` },
      registrationNumber: { schema: cpf, label: msg`CPF` },
      'amortization.requestedAmount': { schema: cents, label: msg`Saldo FGTS` },
      'amortization.paymentMonth': {
        schema: z.enum(['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']),
        label: msg`Mês do aniversário`,
      },
    },
  );

/** Condições do crédito pessoal e do FGTS: cálculo, valor, taxa, prazo e datas. Não simula. */
export const creditConditionsFill = (prefix: string) =>
  fill(
    `${prefix}.fillConditions`,
    msg`Etapa Condições: preenche, como rascunho, o tipo de cálculo (Gross, Liquid ou Payment, conforme o produto), o valor solicitado ou da parcela (centavos), a taxa (fração em texto: '0.0185' = 1,85%), a quantidade de parcelas ou a duração (conforme o produto), a data de início, a data do primeiro pagamento e o dia de vencimento. Campos que o produto não mostra são recusados com o motivo. Não simula: o Simular é da pessoa.`,
    {
      'amortization.calculateByValueType': { schema: z.enum(['Gross', 'Liquid', 'Payment']), label: msg`Tipo de cálculo` },
      'amortization.requestedAmount': { schema: cents, label: msg`Valor solicitado` },
      'amortization.apr': { schema: z.string().regex(/^\d+(\.\d+)?$/), label: msg`Taxa de Juros` },
      'amortization.numberOfPayments': { schema: positiveInt, label: msg`Quantidade de parcelas` },
      'amortization.termInMonths': { schema: positiveInt, label: msg`Duração` },
      'amortization.startDate': { schema: civilDate, label: msg`Data de início` },
      'amortization.firstPaymentDate': { schema: civilDate, label: msg`Data do primeiro pagamento` },
      'amortization.paymentDay': { schema: z.number().int().min(1).max(31), label: msg`Dia de vencimento` },
    },
  );

/** "Continuar" pelo Luy: só nas etapas que não simulam, não consultam e não abrem o Resumo (X01). */
export const hireNext = (prefix: string) =>
  action<{ readonly step: string }>({
    id: `${prefix}.next`,
    kind: 'draft',
    label: msg`Avançar para a próxima etapa`,
    description: msg`Avança a contratação para a próxima etapa, como o Continuar, só quando a etapa atual não simula, não consulta margem nem ofertas e não leva ao Resumo. Passe step com o id da etapa atual (o contexto da tela traz). Nas outras, a resposta diz por quê e o Continuar é da pessoa.`,
    permission: SIMULATION_READ,
    input: z.strictObject({ step: z.string().min(1) }),
    run: mountedRequired,
  });
